import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useAction } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { usePermissions } from '@/hooks/useAuth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Loading } from '@/components/ui/loading'
import { AlertCircle, ArrowLeft, Building, LockKeyhole, Plus, Save } from 'lucide-react'
import { Link } from 'react-router-dom'
import {Id} from "../../../convex/_generated/dataModel";
import {
    getCompanyNameValidationError,
    toPrimoUpSlug,
} from "../../../shared/companyIdentifiers";
import {
    getRequiredCompanyCreationFieldErrors,
    isDuplicateCompanySlugError,
} from '../../../shared/companyCreationValidation'

interface CompanyFormData {
    nome: string
    indirizzo: string
    citta: string
    cap: string
    provincia: string
    partita_iva: string
    codice_fiscale: string
    telefono: string
    email: string
    pec: string
    sdi: string
    customer_type_key: string
    suffix: string
    slug: string
}

export default function CompanyForm() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEdit = !!id
    const [searchParams] = useSearchParams()

    const { hasPermission } = usePermissions()
    const canManageCompanies = hasPermission('manage_companies')
    const [organizationId, setOrganizationId] = useState(searchParams.get('organizationId') || '')
    const organizations = useQuery(api.organizations.listOrganizations, canManageCompanies ? {} : 'skip')

    const [formData, setFormData] = useState<CompanyFormData>({
        nome: '',
        indirizzo: '',
        citta: '',
        cap: '',
        provincia: '',
        partita_iva: '',
        codice_fiscale: '',
        telefono: '',
        email: '',
        pec: '',
        sdi: '',
        customer_type_key: '',
        suffix: '',
        slug: ''
    })

    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState<Partial<CompanyFormData>>({})
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false)
    const [slugUnlockedAfterConflict, setSlugUnlockedAfterConflict] = useState(false)
    const [showNewCustomerType, setShowNewCustomerType] = useState(false)
    const [newCustomerTypeName, setNewCustomerTypeName] = useState('')
    const [newCustomerTypeError, setNewCustomerTypeError] = useState<string | null>(null)
    const [savingCustomerType, setSavingCustomerType] = useState(false)
    const [justCreatedCustomerType, setJustCreatedCustomerType] = useState<{ key: string; name: string } | null>(null)
    const [customerTypeStatus, setCustomerTypeStatus] = useState<string | null>(null)
    const newCustomerTypeInputRef = useRef<HTMLInputElement>(null)
    const customerTypeSelectRef = useRef<HTMLSelectElement>(null)
    const newCustomerTypeTriggerRef = useRef<HTMLButtonElement>(null)

    // Get company data if editing
    const company = useQuery(
        api.companies.getCompany,
        isEdit ? { companyId: id as Id<"companies"> } : "skip"
    )

    // Get company slug if editing
    const companySlug = useQuery(
        api.companies.getCompanySlug,
        isEdit && id ? { companyId: id as any } : "skip"
    )

    const customerTypes = useQuery(
        api.companies.listCustomerTypes,
        canManageCompanies ? {} : "skip"
    )
    const hasImmutableSlug = isEdit && Boolean(companySlug?.slug)
    const needsLegacySlug = isEdit && companySlug === null
    const isNewCompanySlugLocked = !isEdit && !slugUnlockedAfterConflict
    const isSlugReadOnly = hasImmutableSlug || isNewCompanySlugLocked

    // Mutations and Actions
    const createCompany = useAction(api.primoup.createCompanyWithPrimoUp)
    const updateCompany = useMutation(api.companies.updateCompany)
    const updateClinicEmail = useAction(api.primoup_clinic_update.updateClinicEmail)
    const addCustomerType = useMutation(api.companies.addCustomerType)

    const customerTypeOptions = customerTypes
        ? justCreatedCustomerType && !customerTypes.some((item) => item.key === justCreatedCustomerType.key)
            ? [...customerTypes, { ...justCreatedCustomerType, is_default: false }]
            : customerTypes
        : []

    // Load company data when editing
    useEffect(() => {
        if (isEdit && company) {
            setOrganizationId(company.organization_id || '')
            setFormData({
                nome: company.nome || '',
                indirizzo: company.indirizzo || '',
                citta: company.citta || '',
                cap: company.cap || '',
                provincia: company.provincia || '',
                partita_iva: company.partita_iva || '',
                codice_fiscale: company.codice_fiscale || '',
                telefono: company.telefono || '',
                email: company.email || '',
                pec: company.pec || '',
                sdi: company.sdi || '',
                customer_type_key: company.customer_type_key || '',
                suffix: company.suffix || '',
                slug: companySlug?.slug || ''
            })
        }
    }, [isEdit, company, companySlug])

    useEffect(() => {
        if (!showNewCustomerType) return
        requestAnimationFrame(() => newCustomerTypeInputRef.current?.focus())
    }, [showNewCustomerType])

    if (!canManageCompanies) {
        return (
            <div className="container mx-auto px-4 py-8">
                <Card>
                    <CardContent className="p-8 text-center">
                        <Building className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                        <h2 className="text-xl font-semibold mb-2">Accesso Negato</h2>
                        <p className="text-gray-600">Non hai i permessi per gestire le cliniche.</p>
                    </CardContent>
                </Card>
            </div>
        )
    }

    if (isEdit && (company === undefined || companySlug === undefined)) {
        return <Loading />
    }

    const clearFieldError = (field: keyof CompanyFormData) => {
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }))
        }
    }

    const handleInputChange = (field: keyof CompanyFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }))
        setSubmitError(null)
        clearFieldError(field)
    }

    const handleCompanyNameChange = (value: string) => {
        setFormData(prev => ({
            ...prev,
            nome: value,
            ...(!isEdit && !slugManuallyEdited ? { slug: toPrimoUpSlug(value) } : {}),
        }))
        setSubmitError(null)
        setErrors(prev => ({
            ...prev,
            nome: undefined,
            ...(!isEdit && !slugManuallyEdited ? { slug: undefined } : {}),
        }))
    }

    const handleSlugChange = (value: string) => {
        setSlugManuallyEdited(true)
        handleInputChange('slug', value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
    }

    const regenerateSlugFromName = () => {
        setSlugManuallyEdited(false)
        handleInputChange('slug', toPrimoUpSlug(formData.nome))
    }

    const focusFirstInvalidField = (fieldErrors: Partial<CompanyFormData>) => {
        const firstInvalidField = Object.keys(fieldErrors)[0]
        requestAnimationFrame(() => {
            const targetId = firstInvalidField === 'customer_type_key'
                ? 'company-customer-type'
                : firstInvalidField
                    ? `company-${firstInvalidField}`
                    : 'company-submit-error'
            const target = firstInvalidField
                ? document.getElementById(targetId)
                : document.getElementById('company-submit-error')
            target?.focus()
            target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
    }

    const handleAddCustomerType = async () => {
        if (savingCustomerType || loading) return
        const name = newCustomerTypeName.trim()
        if (name.length < 2) {
            setNewCustomerTypeError('Inserisci almeno 2 caratteri.')
            newCustomerTypeInputRef.current?.focus()
            return
        }

        setSavingCustomerType(true)
        setNewCustomerTypeError(null)
        try {
            const result = await addCustomerType({ name })
            setJustCreatedCustomerType({ key: result.key, name: result.name })
            handleInputChange('customer_type_key', result.key)
            setNewCustomerTypeName('')
            setShowNewCustomerType(false)
            setCustomerTypeStatus(`Tipologia “${result.name}” creata e selezionata.`)
            requestAnimationFrame(() => customerTypeSelectRef.current?.focus())
        } catch (error) {
            const rawMessage = error instanceof Error ? error.message : ''
            const knownMessage = [
                'Questa tipologia cliente esiste già',
                'Inserisci un nome tra 2 e 60 caratteri',
                'Nome tipologia non valido',
                'Non hai i permessi per aggiungere tipologie cliente',
            ].find((message) => rawMessage.includes(message))
            setNewCustomerTypeError(knownMessage ?? 'Non è stato possibile aggiungere la tipologia. Riprova.')
        } finally {
            setSavingCustomerType(false)
        }
    }

    const cancelNewCustomerType = () => {
        setShowNewCustomerType(false)
        setNewCustomerTypeName('')
        setNewCustomerTypeError(null)
        requestAnimationFrame(() => newCustomerTypeTriggerRef.current?.focus())
    }

    const validateForm = (): boolean => {
        const newErrors: Partial<CompanyFormData> = isEdit
            ? {}
            : getRequiredCompanyCreationFieldErrors(formData)

        if (isEdit) {
            const companyNameError = getCompanyNameValidationError(formData.nome)
            if (companyNameError) {
                newErrors.nome = companyNameError
            }
            if (!formData.indirizzo.trim()) {
                newErrors.indirizzo = 'Indirizzo è obbligatorio'
            }
            if (!formData.citta.trim()) {
                newErrors.citta = 'Città è obbligatoria'
            }
            if (!formData.cap.trim()) {
                newErrors.cap = 'CAP è obbligatorio'
            } else if (!/^\d{5}$/.test(formData.cap)) {
                newErrors.cap = 'CAP deve essere 5 cifre'
            }
        }

        if (needsLegacySlug) {
            if (!formData.suffix.trim()) {
                newErrors.suffix = 'Suffisso è obbligatorio'
            } else if (!/^[A-Z0-9]{2,5}$/.test(formData.suffix)) {
                newErrors.suffix = 'Suffisso deve essere 2-5 caratteri maiuscoli/numeri'
            }

            const normalizedSlug = toPrimoUpSlug(formData.slug)
            if (!normalizedSlug) {
                newErrors.slug = 'Slug PrimoUp è obbligatorio'
            }
        }

        if (isEdit && formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Email non valida'
        }

        if (isEdit && formData.pec && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.pec)) {
            newErrors.pec = 'PEC non valida'
        }

        if (isEdit && formData.partita_iva && !/^\d{11}$/.test(formData.partita_iva)) {
            newErrors.partita_iva = 'Partita IVA deve essere 11 cifre'
        }

        if (isEdit && formData.codice_fiscale && !/^[A-Z0-9]{11,16}$/.test(formData.codice_fiscale.toUpperCase())) {
            newErrors.codice_fiscale = 'Codice fiscale non valido'
        }

        setErrors(newErrors)
        if (Object.keys(newErrors).length > 0) {
            focusFirstInvalidField(newErrors)
        }
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (loading || savingCustomerType || organizations === undefined) return
        if (organizationId && !organizations.some((organization) => organization._id === organizationId)) {
            setSubmitError('Seleziona una società disponibile prima di salvare la clinica.')
            document.getElementById('company-organization')?.focus()
            return
        }

        if (!validateForm()) {
            return
        }

        setLoading(true)
        setSubmitError(null)

        try {
            const companyData = {
                nome: formData.nome.trim(),
                indirizzo: formData.indirizzo.trim(),
                citta: formData.citta.trim(),
                cap: formData.cap.trim(),
                provincia: formData.provincia.trim() || undefined,
                partita_iva: formData.partita_iva.trim() || undefined,
                codice_fiscale: formData.codice_fiscale.trim().toUpperCase() || undefined,
                telefono: formData.telefono.trim() || undefined,
                email: formData.email.trim() || undefined,
                pec: formData.pec.trim() || undefined,
                sdi: formData.sdi.trim() || undefined,
            }

            if (isEdit) {
                const result = await updateCompany({
                    companyId: id! as Id<"companies">,
                    ...companyData,
                    organization_id: organizationId ? organizationId as Id<'organizations'> : null,
                    customer_type_key: formData.customer_type_key || null,
                    legacy_slug: needsLegacySlug
                        ? toPrimoUpSlug(formData.slug || formData.nome)
                        : undefined,
                })

                if (result.contactChanged) {
                    try {
                        await updateClinicEmail({
                            companyId: id as Id<"companies">,
                            newEmail: result.newEmail,
                            newPhone: result.newPhone,
                        })
                        console.log('✅ Clinic email updated in PrimoUp')
                    } catch (primoUpError) {
                        console.error('❌ Failed to update PrimoUp clinic email:', primoUpError)
                        setSubmitError(
                            'I dati sono stati aggiornati in FastLab, ma il contatto non è stato sincronizzato con PrimoUp. Contatta l’assistenza prima di riprovare.'
                        )
                        return
                    }
                }
            } else {
                const result = await createCompany({
                    ...companyData,
                    organization_id: organizationId ? organizationId as Id<'organizations'> : undefined,
                    customer_type_key: formData.customer_type_key || undefined,
                    suffix: formData.suffix.trim().toUpperCase(),
                    slug: toPrimoUpSlug(formData.slug),
                })
                if (!result.ok) {
                    const fieldErrors = result.field_errors as Partial<CompanyFormData>
                    if (isDuplicateCompanySlugError(fieldErrors.slug)) {
                        setSlugUnlockedAfterConflict(true)
                    }
                    setErrors(fieldErrors)
                    setSubmitError(result.message)
                    focusFirstInvalidField(fieldErrors)
                    return
                }
            }

            navigate('/companies')
        } catch (error) {
            console.error('Errore salvataggio clinica:', error)
            const message = error instanceof Error && error.message.length <= 260
                ? error.message
                : 'Non è stato possibile salvare la clinica. Controlla la connessione e riprova.'
            setSubmitError(message)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="container mx-auto px-4 py-8">
            {/* Header */}
            <div className="mb-8 flex items-start gap-3">
                <Button asChild variant="outline" size="icon" className="shrink-0">
                    <Link to="/companies" aria-label="Torna alle cliniche">
                        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                    </Link>
                </Button>
                <div className="min-w-0">
                    <h1 className="flex items-start gap-2 text-2xl font-bold tracking-[-0.02em]">
                        <Building className="mt-0.5 h-6 w-6 shrink-0" aria-hidden="true" />
                        <span>{isEdit ? 'Modifica clinica' : 'Nuova clinica'}</span>
                    </h1>
                    <p className="text-gray-600 mt-2">
                        {isEdit ? 'Modifica i dati della clinica e la società di appartenenza' : 'Inserisci i dati della nuova clinica'}
                    </p>
                </div>
            </div>

            {/* Form */}
            <Card>
                <CardHeader>
                    <CardTitle>Dati clinica</CardTitle>
                    {!isEdit && (
                        <p className="text-sm leading-5 text-gray-600">
                            I dati della clinica sono obbligatori. Slug e suffisso diventeranno definitivi dopo la creazione.
                        </p>
                    )}
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} noValidate className="space-y-6">
                        <div className="border-b border-gray-200 pb-6">
                            <label htmlFor="company-organization" className="mb-2 block text-sm font-medium">Società di appartenenza</label>
                            <select
                                id="company-organization"
                                value={organizationId}
                                onChange={(event) => { setOrganizationId(event.target.value); setSubmitError(null) }}
                                disabled={loading || organizations === undefined}
                                aria-describedby="company-organization-hint"
                                className="flex min-h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <option value="">{organizations === undefined ? 'Caricamento società…' : isEdit ? 'Nessuna società collegata' : 'Crea automaticamente una società con lo stesso nome'}</option>
                                {organizations?.map((organization) => <option key={organization._id} value={organization._id}>{organization.nome}</option>)}
                            </select>
                            <p id="company-organization-hint" className="mt-2 text-sm leading-6 text-gray-600">
                                {isEdit
                                    ? 'Cambiare società può modificare i listini ereditati dalla clinica. Gli accessi degli utenti restano invariati.'
                                    : 'Seleziona la società che possiede la clinica oppure lascia la creazione automatica. La condivisione dei listini è disattivata per le nuove società.'}
                            </p>
                            {isEdit && organizationId && <Link to={`/organizations/${organizationId}/edit`} className="mt-2 inline-block text-sm font-medium text-gray-900 underline underline-offset-4 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary">Apri società</Link>}
                        </div>
                        {submitError && (
                            <div
                                id="company-submit-error"
                                tabIndex={-1}
                                role="alert"
                                className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-950 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                            >
                                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold">
                                        {isEdit ? 'Aggiornamento incompleto' : 'Società non creata'}
                                    </p>
                                    <p className="mt-0.5 text-sm leading-5">{submitError}</p>
                                </div>
                            </div>
                        )}

                        {/* Nome e Suffisso */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-2">
                                <label htmlFor="company-nome" className="block text-sm font-medium mb-2">
                                    Nome clinica *
                                </label>
                                <Input
                                    id="company-nome"
                                    value={formData.nome}
                                    onChange={(e) => handleCompanyNameChange(e.target.value)}
                                    placeholder="Nome della clinica"
                                    required
                                    className={errors.nome ? 'border-red-500' : ''}
                                    aria-invalid={!!errors.nome}
                                    aria-describedby={errors.nome ? 'company-nome-error' : undefined}
                                />
                                {errors.nome && (
                                    <p id="company-nome-error" className="text-red-700 text-sm mt-1">{errors.nome}</p>
                                )}
                            </div>
                            <div>
                                <label htmlFor="company-suffix" className="block text-sm font-medium mb-2">
                                    Suffisso *
                                </label>
                                <Input
                                    id="company-suffix"
                                    value={formData.suffix}
                                    onChange={(e) => handleInputChange('suffix', e.target.value.toUpperCase())}
                                    placeholder="ABC"
                                    maxLength={5}
                                    required={!isEdit}
                                    readOnly={isEdit}
                                    aria-readonly={isEdit}
                                    aria-invalid={!!errors.suffix}
                                    aria-describedby={errors.suffix ? 'company-suffix-error' : 'company-suffix-hint'}
                                    className={`${errors.suffix ? 'border-red-500' : ''} ${isEdit ? 'bg-gray-50 text-gray-600' : ''}`}
                                />
                                {errors.suffix && (
                                    <p id="company-suffix-error" className="text-red-700 text-sm mt-1">{errors.suffix}</p>
                                )}
                                <p id="company-suffix-hint" className="mt-1 flex items-start gap-1 text-xs text-gray-500">
                                    {isEdit && <LockKeyhole className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />}
                                    <span>{isEdit ? 'Definitivo dopo la creazione.' : 'Usato per numerare le prescrizioni.'}</span>
                                </p>
                            </div>
                        </div>

                        {/* Slug */}
                        <div>
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                <label htmlFor="company-slug" className="block text-sm font-medium">
                                    Slug PrimoUp *
                                </label>
                                {!isEdit && slugUnlockedAfterConflict && slugManuallyEdited && (
                                    <button
                                        type="button"
                                        onClick={regenerateSlugFromName}
                                        className="text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                    >
                                        Rigenera dal nome
                                    </button>
                                )}
                            </div>
                            <Input
                                id="company-slug"
                                value={formData.slug}
                                onChange={(e) => handleSlugChange(e.target.value)}
                                placeholder="slug-clinica"
                                required={!hasImmutableSlug}
                                readOnly={isSlugReadOnly}
                                aria-readonly={isSlugReadOnly}
                                aria-invalid={!!errors.slug}
                                aria-describedby={`${errors.slug ? 'company-slug-error ' : ''}company-slug-hint`}
                                className={`${errors.slug ? 'border-red-500' : ''} ${isSlugReadOnly ? 'bg-gray-50 text-gray-600' : ''}`}
                            />
                            {errors.slug && (
                                <p id="company-slug-error" className="text-red-700 text-sm mt-1">{errors.slug}</p>
                            )}
                            <p id="company-slug-hint" className="mt-1 flex items-start gap-1 text-xs text-gray-500">
                                {isSlugReadOnly && <LockKeyhole className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />}
                                <span>
                                    {hasImmutableSlug
                                        ? 'Identificativo PrimoUp definitivo dopo la creazione.'
                                        : needsLegacySlug
                                            ? 'Società legacy: inseriscilo ora. Dopo il salvataggio non sarà più modificabile.'
                                            : slugUnlockedAfterConflict
                                                ? slugManuallyEdited
                                                    ? 'Slug personalizzato. Usa solo lettere minuscole, numeri e trattini.'
                                                    : 'Lo slug proposto era già utilizzato. Modificalo e riprova.'
                                                : 'Generato automaticamente dal nome. Potrai modificarlo solo se risulta già utilizzato.'}
                                </span>
                            </p>
                        </div>

                        <div>
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                <label htmlFor="company-customer-type" className="block text-sm font-medium">
                                    Tipologia cliente{!isEdit ? ' *' : ''}
                                </label>
                                {!isEdit && (
                                    <Button
                                        ref={newCustomerTypeTriggerRef}
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            setShowNewCustomerType((current) => !current)
                                            setNewCustomerTypeError(null)
                                            setCustomerTypeStatus(null)
                                        }}
                                        aria-expanded={showNewCustomerType}
                                        aria-controls="company-new-customer-type-panel"
                                        disabled={loading || savingCustomerType || customerTypes === undefined}
                                    >
                                        <Plus
                                            className={`mr-1.5 h-4 w-4 transition-transform ${showNewCustomerType ? 'rotate-45' : ''}`}
                                            aria-hidden="true"
                                        />
                                        {showNewCustomerType ? 'Chiudi' : 'Nuova tipologia'}
                                    </Button>
                                )}
                            </div>
                            <select
                                ref={customerTypeSelectRef}
                                id="company-customer-type"
                                value={formData.customer_type_key}
                                onChange={(e) => {
                                    handleInputChange('customer_type_key', e.target.value)
                                    setCustomerTypeStatus(null)
                                }}
                                disabled={customerTypes === undefined || savingCustomerType || loading}
                                required={!isEdit}
                                aria-required={!isEdit}
                                aria-invalid={!!errors.customer_type_key}
                                aria-describedby={errors.customer_type_key ? 'company-customer-type-error' : 'company-customer-type-hint'}
                                className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background outline-hidden transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${errors.customer_type_key ? 'border-red-500' : 'border-input'}`}
                            >
                                <option value="">
                                    {customerTypes === undefined
                                        ? 'Caricamento tipologie…'
                                        : isEdit
                                            ? 'Non assegnata'
                                            : 'Seleziona una tipologia…'}
                                </option>
                                {customerTypeOptions.map((customerType) => (
                                    <option key={customerType.key} value={customerType.key}>
                                        {customerType.name}
                                    </option>
                                ))}
                            </select>
                            {errors.customer_type_key && (
                                <p id="company-customer-type-error" className="mt-1 text-sm text-red-700">
                                    {errors.customer_type_key}
                                </p>
                            )}
                            <p id="company-customer-type-hint" className="mt-1 text-xs text-gray-500">
                                {!isEdit
                                    ? 'Obbligatoria. Viene usata per segmentare i clienti nelle Analytics.'
                                    : 'Usata per segmentare i clienti nelle Analytics.'}
                            </p>
                            {customerTypeStatus && (
                                <p role="status" className="mt-1 text-sm text-green-700">
                                    {customerTypeStatus}
                                </p>
                            )}

                            {!isEdit && showNewCustomerType && (
                                <div id="company-new-customer-type-panel" className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                                    <label htmlFor="company-new-customer-type" className="block text-sm font-medium text-gray-900">
                                        Nome nuova tipologia
                                    </label>
                                    <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                                        <Input
                                            ref={newCustomerTypeInputRef}
                                            id="company-new-customer-type"
                                            value={newCustomerTypeName}
                                            onChange={(event) => {
                                                setNewCustomerTypeName(event.target.value)
                                                setNewCustomerTypeError(null)
                                            }}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter') {
                                                    event.preventDefault()
                                                    void handleAddCustomerType()
                                                }
                                                if (event.key === 'Escape') {
                                                    cancelNewCustomerType()
                                                }
                                            }}
                                            placeholder="Es. Centro odontoiatrico"
                                            maxLength={60}
                                            disabled={savingCustomerType || loading}
                                            aria-invalid={!!newCustomerTypeError}
                                            aria-describedby={newCustomerTypeError ? 'company-new-customer-type-error' : undefined}
                                            className={newCustomerTypeError ? 'border-red-500 sm:flex-1' : 'sm:flex-1'}
                                        />
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                onClick={() => void handleAddCustomerType()}
                                                disabled={savingCustomerType || loading || newCustomerTypeName.trim().length < 2}
                                                className="flex-1 sm:flex-none"
                                            >
                                                {savingCustomerType ? 'Aggiunta…' : 'Aggiungi e seleziona'}
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={cancelNewCustomerType}
                                                disabled={savingCustomerType || loading}
                                            >
                                                Annulla
                                            </Button>
                                        </div>
                                    </div>
                                    {newCustomerTypeError && (
                                        <p id="company-new-customer-type-error" role="alert" className="mt-2 text-sm text-red-700">
                                            {newCustomerTypeError}
                                        </p>
                                    )}
                                    <p className="mt-2 text-xs leading-5 text-gray-500">
                                        La nuova tipologia sarà disponibile anche per le prossime cliniche.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Indirizzo */}
                        <div>
                            <label htmlFor="company-indirizzo" className="block text-sm font-medium mb-2">
                                Indirizzo *
                            </label>
                            <Input
                                id="company-indirizzo"
                                value={formData.indirizzo}
                                onChange={(e) => handleInputChange('indirizzo', e.target.value)}
                                placeholder="Via, Piazza, etc."
                                required
                                className={errors.indirizzo ? 'border-red-500' : ''}
                                aria-invalid={!!errors.indirizzo}
                                aria-describedby={errors.indirizzo ? 'company-indirizzo-error' : undefined}
                            />
                            {errors.indirizzo && (
                                <p id="company-indirizzo-error" className="text-red-700 text-sm mt-1">{errors.indirizzo}</p>
                            )}
                        </div>

                        {/* Città, CAP, Provincia */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label htmlFor="company-citta" className="block text-sm font-medium mb-2">
                                    Città *
                                </label>
                                <Input
                                    id="company-citta"
                                    value={formData.citta}
                                    onChange={(e) => handleInputChange('citta', e.target.value)}
                                    placeholder="Milano"
                                    required
                                    className={errors.citta ? 'border-red-500' : ''}
                                    aria-invalid={!!errors.citta}
                                    aria-describedby={errors.citta ? 'company-citta-error' : undefined}
                                />
                                {errors.citta && (
                                    <p id="company-citta-error" className="text-red-700 text-sm mt-1">{errors.citta}</p>
                                )}
                            </div>
                            <div>
                                <label htmlFor="company-cap" className="block text-sm font-medium mb-2">
                                    CAP *
                                </label>
                                <Input
                                    id="company-cap"
                                    value={formData.cap}
                                    onChange={(e) => handleInputChange('cap', e.target.value)}
                                    placeholder="20100"
                                    maxLength={5}
                                    inputMode="numeric"
                                    required
                                    className={errors.cap ? 'border-red-500' : ''}
                                    aria-invalid={!!errors.cap}
                                    aria-describedby={errors.cap ? 'company-cap-error' : undefined}
                                />
                                {errors.cap && (
                                    <p id="company-cap-error" className="text-red-700 text-sm mt-1">{errors.cap}</p>
                                )}
                            </div>
                            <div>
                                <label htmlFor="company-provincia" className="block text-sm font-medium mb-2">
                                    Provincia{!isEdit ? ' *' : ''}
                                </label>
                                <Input
                                    id="company-provincia"
                                    value={formData.provincia}
                                    onChange={(e) => handleInputChange('provincia', e.target.value.toUpperCase())}
                                    placeholder="MI"
                                    maxLength={2}
                                    required={!isEdit}
                                    aria-required={!isEdit}
                                    className={errors.provincia ? 'border-red-500' : ''}
                                    aria-invalid={!!errors.provincia}
                                    aria-describedby={errors.provincia ? 'company-provincia-error' : undefined}
                                />
                                {errors.provincia && (
                                    <p id="company-provincia-error" className="mt-1 text-sm text-red-700">{errors.provincia}</p>
                                )}
                            </div>
                        </div>

                        {/* Partita IVA e Codice Fiscale */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="company-partita_iva" className="block text-sm font-medium mb-2">
                                    Partita IVA{!isEdit ? ' *' : ''}
                                </label>
                                <Input
                                    id="company-partita_iva"
                                    value={formData.partita_iva}
                                    onChange={(e) => handleInputChange('partita_iva', e.target.value)}
                                    placeholder="12345678901"
                                    maxLength={11}
                                    inputMode="numeric"
                                    required={!isEdit}
                                    aria-required={!isEdit}
                                    className={errors.partita_iva ? 'border-red-500' : ''}
                                    aria-invalid={!!errors.partita_iva}
                                    aria-describedby={errors.partita_iva ? 'company-partita-iva-error' : undefined}
                                />
                                {errors.partita_iva && (
                                    <p id="company-partita-iva-error" className="text-red-700 text-sm mt-1">{errors.partita_iva}</p>
                                )}
                            </div>
                            <div>
                                <label htmlFor="company-codice_fiscale" className="block text-sm font-medium mb-2">
                                    Codice Fiscale{!isEdit ? ' *' : ''}
                                </label>
                                <Input
                                    id="company-codice_fiscale"
                                    value={formData.codice_fiscale}
                                    onChange={(e) => handleInputChange('codice_fiscale', e.target.value.toUpperCase())}
                                    placeholder="ABCDEF12G34H567I"
                                    maxLength={16}
                                    required={!isEdit}
                                    aria-required={!isEdit}
                                    className={errors.codice_fiscale ? 'border-red-500' : ''}
                                    aria-invalid={!!errors.codice_fiscale}
                                    aria-describedby={errors.codice_fiscale ? 'company-codice-fiscale-error' : undefined}
                                />
                                {errors.codice_fiscale && (
                                    <p id="company-codice-fiscale-error" className="text-red-700 text-sm mt-1">{errors.codice_fiscale}</p>
                                )}
                            </div>
                        </div>

                        {/* Contatti */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="company-telefono" className="block text-sm font-medium mb-2">
                                    Telefono{!isEdit ? ' *' : ''}
                                </label>
                                <Input
                                    id="company-telefono"
                                    type="tel"
                                    value={formData.telefono}
                                    onChange={(e) => handleInputChange('telefono', e.target.value)}
                                    placeholder="+39 02 1234567"
                                    required={!isEdit}
                                    aria-required={!isEdit}
                                    className={errors.telefono ? 'border-red-500' : ''}
                                    aria-invalid={!!errors.telefono}
                                    aria-describedby={errors.telefono ? 'company-telefono-error' : undefined}
                                />
                                {errors.telefono && (
                                    <p id="company-telefono-error" className="mt-1 text-sm text-red-700">{errors.telefono}</p>
                                )}
                            </div>
                            <div>
                                <label htmlFor="company-email" className="block text-sm font-medium mb-2">
                                    Email{!isEdit ? ' *' : ''}
                                </label>
                                <Input
                                    id="company-email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    placeholder="info@clinica.it"
                                    required={!isEdit}
                                    aria-required={!isEdit}
                                    className={errors.email ? 'border-red-500' : ''}
                                    aria-invalid={!!errors.email}
                                    aria-describedby={errors.email ? 'company-email-error' : undefined}
                                />
                                {errors.email && (
                                    <p id="company-email-error" className="text-red-700 text-sm mt-1">{errors.email}</p>
                                )}
                            </div>
                        </div>

                        {/* PEC e SDI */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="company-pec" className="block text-sm font-medium mb-2">
                                    PEC{!isEdit ? ' *' : ''}
                                </label>
                                <Input
                                    id="company-pec"
                                    type="email"
                                    value={formData.pec}
                                    onChange={(e) => handleInputChange('pec', e.target.value)}
                                    placeholder="pec@clinica.it"
                                    required={!isEdit}
                                    aria-required={!isEdit}
                                    className={errors.pec ? 'border-red-500' : ''}
                                    aria-invalid={!!errors.pec}
                                    aria-describedby={errors.pec ? 'company-pec-error' : undefined}
                                />
                                {errors.pec && (
                                    <p id="company-pec-error" className="mt-1 text-sm text-red-700">{errors.pec}</p>
                                )}
                            </div>
                            <div>
                                <label htmlFor="company-sdi" className="block text-sm font-medium mb-2">
                                    Codice SDI{!isEdit ? ' *' : ''}
                                </label>
                                <Input
                                    id="company-sdi"
                                    value={formData.sdi}
                                    onChange={(e) => handleInputChange('sdi', e.target.value.toUpperCase())}
                                    placeholder="ABCDEFG"
                                    maxLength={7}
                                    required={!isEdit}
                                    aria-required={!isEdit}
                                    className={errors.sdi ? 'border-red-500' : ''}
                                    aria-invalid={!!errors.sdi}
                                    aria-describedby={errors.sdi ? 'company-sdi-error' : undefined}
                                />
                                {errors.sdi && (
                                    <p id="company-sdi-error" className="mt-1 text-sm text-red-700">{errors.sdi}</p>
                                )}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col-reverse gap-3 border-t pt-6 sm:flex-row sm:justify-end">
                            <Button asChild variant="outline">
                                <Link to="/companies">Annulla</Link>
                            </Button>
                            <Button
                                type="submit"
                                disabled={loading || savingCustomerType || customerTypes === undefined || organizations === undefined}
                            >
                                <Save className="w-4 h-4 mr-2" />
                                {loading
                                    ? isEdit
                                        ? 'Salvataggio…'
                                        : 'Creazione su PrimoUp e FastLab…'
                                    : isEdit
                                        ? 'Aggiorna clinica'
                                        : 'Crea clinica'}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    )
}
